/**
 * useActivityTracker
 * --------------------------------
 * Starts an activity session on mount (when authenticated) and sends
 * a heartbeat every 30s while the tab is visible. Closes the session
 * on unmount / pagehide. Server-side, idle sessions (>2 min without
 * heartbeat) are treated as ended for reporting.
 */
import { useEffect, useRef } from "react";
import { getSupabase } from "@/lib/supabase-lazy";
import { useAuthState } from "./useAuthState";

const HEARTBEAT_MS = 30_000;

export function useActivityTracker() {
  const { user } = useAuthState();
  const sessionIdRef = useRef<string | null>(null);
  const lastTickRef = useRef<number>(Date.now());
  const visibleRef = useRef<boolean>(typeof document !== "undefined" ? document.visibilityState === "visible" : true);

  useEffect(() => {
    if (!user) return;
    let cancelled = false;

    (async () => {
      const ua = typeof navigator !== "undefined" ? navigator.userAgent.slice(0, 500) : undefined;
      const supabase = await getSupabase();
      const { data, error } = await supabase.rpc("start_activity_session", {
        ...(ua !== undefined ? { _user_agent: ua } : {}),
      });
      if (!cancelled && !error && data) {
        sessionIdRef.current = data as unknown as string;
        lastTickRef.current = Date.now();
      }
    })();

    const sendHeartbeat = async (extraEnd = false) => {
      const sid = sessionIdRef.current;
      if (!sid) return;
      const now = Date.now();
      const delta = Math.min(Math.round((now - lastTickRef.current) / 1000), 120); // cap 2 min
      lastTickRef.current = now;
      const supabase = await getSupabase();
      if (extraEnd) {
        await supabase.rpc("end_activity_session", { _session_id: sid, _seconds_delta: delta });
      } else if (visibleRef.current) {
        await supabase.rpc("heartbeat_activity_session", { _session_id: sid, _seconds_delta: delta });
      }
    };

    const interval = setInterval(() => { void sendHeartbeat(false); }, HEARTBEAT_MS);

    const onVisibility = () => {
      visibleRef.current = document.visibilityState === "visible";
      if (visibleRef.current) lastTickRef.current = Date.now();
      else void sendHeartbeat(false);
    };
    const onPageHide = () => { void sendHeartbeat(true); };

    document.addEventListener("visibilitychange", onVisibility);
    window.addEventListener("pagehide", onPageHide);
    window.addEventListener("beforeunload", onPageHide);

    return () => {
      cancelled = true;
      clearInterval(interval);
      document.removeEventListener("visibilitychange", onVisibility);
      window.removeEventListener("pagehide", onPageHide);
      window.removeEventListener("beforeunload", onPageHide);
      void sendHeartbeat(true);
    };
  }, [user?.id]);
}
